import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";

import { ICountry, UserProfile, useAuthContext } from "@ascendnow/shared";

import AscButton from "../AscButton/AscButton";
import AscModal, { AscModalProps } from "../AscModal/AscModal";
import styles from "./AscUpdateUserModal.module.scss";
import AscTextInput from "../AscInputFields/AscTextInput/AscTextInput";
import AscDateInput from "../AscInputFields/AscDateInput/AscDateInput";
import AscSelectInput from "../AscInputFields/AscSelectInput/AscSelectInput";

export interface AscUpdateUserModalProps
  extends Omit<AscModalProps, "children"> {
  className?: string;
  initialProfileValues?: UserProfile;
  onSave: (userRegisterInfo: UserProfile) => void;
  onCancel: () => void;
  loading?: boolean;
  countriesList?: ICountry[];
}

export default function AscUpdateUserModal({
  className,
  initialProfileValues,
  onSave,
  onCancel,
  loading = false,
  countriesList,
  ...rest
}: AscUpdateUserModalProps) {
  const { user } = useAuthContext();
  const formik = useFormik<UserProfile>({
    initialValues: {
      name: initialProfileValues?.name ? initialProfileValues?.name : "",
      school: initialProfileValues?.school ? initialProfileValues?.school : "",
      birthDate: initialProfileValues?.birthDate
        ? dayjs(initialProfileValues?.birthDate).format("DD/MM/YYYY")
        : "",
      country: initialProfileValues?.country
        ? initialProfileValues?.country
        : "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      school: Yup.string().required("Organization is required"),
      birthDate: Yup.string().required("Birth Date is required"),
      country: Yup.string().required("Country is required"),
    }),
    onSubmit: (values) => {
      onSave({
        name: values.name,
        school: values.school,
        birthDate: dayjs(values.birthDate, "DD/MM/YYYY").toISOString(),
        country: values.country,
      });
    },
  });

  function handleCancelClick() {
    formik.resetForm();
    onCancel();
  }

  // useEffect(() => {
  //   if (initialProfileValues) {
  //     formik.setValues({
  //       name: initialProfileValues?.name,
  //       school: initialProfileValues?.school,
  //       birthDate: dayjs(initialProfileValues?.birthDate).format("DD/MM/YYYY"),
  //       country: initialProfileValues?.country,
  //     } as Required<UserProfile>);
  //   }
  // }, [initialProfileValues]);

  return (
    <AscModal
      modalClassName={styles.ground}
      header={"Edit Profile"}
      headerClassName={styles.header}
      innerContentClassName={styles.innerContent}
      testId={"asc-update-profile-modal"}
      {...rest}
    >
      <form onSubmit={formik?.handleSubmit} className={styles.form}>
        <div className={styles.fields}>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="name">
              Name
            </label>
            <AscTextInput
              className={styles.inputField}
              placeholder="James Jones"
              id="name"
              name="name"
              data-testid="asc-update-profile-name"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.name}
            />
            {formik.errors.name && formik.touched.name && (
              <div className={styles.fieldError}>{formik.errors.name}</div>
            )}
          </div>
          {user && user?.student && !user?.student?.isSchoolStudent && (
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="school">
                Organization
              </label>
              <AscTextInput
                className={styles.inputField}
                placeholder="LA Art School"
                id="school"
                name="school"
                data-testid="asc-update-profile-school"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.school}
              />
              {formik.errors.school && formik.touched.school && (
                <div className={styles.fieldError}>{formik.errors.school}</div>
              )}
            </div>
          )}
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="birthDate">
              Birth Date
            </label>
            <AscDateInput
              className={styles.inputField}
              placeholder="DD/MM/YYYY"
              id="birthDate"
              name="birthDate"
              data-testid="asc-update-profile-birthDate"
              setFieldValue={formik.setFieldValue}
              onBlur={formik.handleBlur}
              range={false}
              disabled={!rest?.visible}
              value={formik.values.birthDate}
            />
            {formik.errors.birthDate && formik.touched.birthDate && (
              <div className={styles.fieldError}>{formik.errors.birthDate}</div>
            )}
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="birthDate">
              Country of Residence
            </label>
            <AscSelectInput
              className={styles.inputField}
              placeholder="United States"
              id="country"
              name="country"
              data-testid="asc-update-profile-country"
              onChange={(value) => formik.setFieldValue("country", value)}
              onBlur={formik.handleBlur}
              disabled={!rest?.visible}
              value={formik.values.country}
            >
              {countriesList?.map((c) => (
                <AscSelectInput.Option value={c?.name} label={c?.name} />
              ))}
            </AscSelectInput>
            {formik.errors.country && formik.touched.country && (
              <div className={styles.fieldError}>{formik.errors.country}</div>
            )}
          </div>
        </div>
        <footer className={styles.footer}>
          <AscButton
            className={styles.footerButton}
            variant="secondary"
            size="large"
            disabled={loading}
            onClick={handleCancelClick}
          >
            Cancel
          </AscButton>
          <AscButton
            className={styles.footerButton}
            variant="primary"
            size="large"
            type="submit"
            loading={loading}
          >
            Save
          </AscButton>
        </footer>
      </form>
    </AscModal>
  );
}
